import { CheckOutlined, DownOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Form, FormInstance, Input, Select, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useModalForm } from "@refinedev/antd";

import {
  useGetDetailBankAcccount,
  useGetSystemBank,
  useGetUserBank,
} from "../../../../../api/bankAccount";
import { buttonStyle } from "../../../../../components/pages/auth/components/styles";
import {
  accountNumberRules,
  branchNumberRules,
  clearFieldErrorMessageOnChange,
  onlyLatinCharsDigitsRule,
} from "../../../../../utils/form.constants";
import { useMetadataCountries } from "../../../../../utils/hooks/useMetadataCountries";
import { useMetadataCurrency } from "../../../../../utils/hooks/useMetadataCurrency";
import { NewBankModalCreate } from "../../../../../components/modal/admin/addBank";
import { useUserDetails } from "../../../../../components/pages/auth/components/auth/hooks";

interface IFormBankProps {
  form: FormInstance<any>;
  bankAccountId: number | null;
  handleCancel: () => void;
  onSubmitBankAccount: (values: any) => void;
  isLoading: boolean;
  actionBankAccount: "ADD" | "EDIT" | null;
  userId: number | undefined;
  notGetUserBank?: boolean;
}

const start = 0;
const end = 10;

const requiredFields = [
  "bankId",
  "accountNumber",
  "currencyCode",
  "branchNumber",
];

const requiredFieldsAddNew = [
  "accountNumber",
  "currencyCode",
  "branchNumber",
  "bankName",
  "bankSwift",
  "bankAddress",
  "bankCountry",
];

const FormBank = ({
  form,
  bankAccountId,
  handleCancel,
  onSubmitBankAccount,
  isLoading,
  actionBankAccount,
  userId,
  notGetUserBank = false,
}: IFormBankProps) => {
  const [searchCurrency, setSearchCurrency] = useState<string>("");

  const { data: detailBankAccount, refetch: refetchDetailBankAccount } =
    useGetDetailBankAcccount(bankAccountId || 0);

  const { metadataCurrency = [], refetch: refetchCurrency } =
    useMetadataCurrency();

  const { metadataCountries = [], refetch: refetchCountries } =
    useMetadataCountries();

  const details = useUserDetails();
  const [addNewBank, setAddNewBank] = useState(false);
  const [params, setParams] = useState({ start, end });
  const [paramSystem, setParamSystem] = useState({ start, end });
  const [searchBank, setSearchBank] = useState<string | undefined>();
  const [bankSelected, setBankSelected] = useState<any>();
  const [isAllFieldsFilled, setIsAllFieldsFilled] = useState(false);
  const [option, setOption] = useState<any>(null);

  const bankId = Form.useWatch("bankId", form);
  const accountNumber = Form.useWatch("accountNumber", form);
  const currencyCode = Form.useWatch("currencyCode", form);
  const branchNumber = Form.useWatch("branchNumber", form);
  const bankName = Form.useWatch("bankName", form);
  const bankSwift = Form.useWatch("bankSwift", form);
  const bankAddress = Form.useWatch("bankAddress", form);
  const bankCountry = Form.useWatch("bankCountry", form);

  // Watch for form reset - when all key fields are undefined/null and modal is open
  const isFormReset = !bankId && !accountNumber && !currencyCode && actionBankAccount !== null;

  const { data: metadataUserBank = { data: [] }, refetch: refetchUserBank } =
    useGetUserBank({
      start: params.start,
      end: params.end,
      name: searchBank,
      userId: userId ?? null,
      enabled: !!userId && !notGetUserBank,
    });

  const {
    data: metadataSystemBank = { data: [] },
    refetch: refetchSystemBank,
  } = useGetSystemBank({
    start: paramSystem.start,
    end: paramSystem.end,
    name: searchBank,
  });

  const handleChangeCurrency = (value: string) => {
    setSearchCurrency(value);
  };

  // Clear bankSelected and reset data when form is reset
  useEffect(() => {
    if (isFormReset && actionBankAccount) {
      setBankSelected(undefined);
      setSearchBank(undefined);
      setParams({ start, end });
      setParamSystem({ start, end });
      setOption(null);
    }
  }, [isFormReset, actionBankAccount]);

  useEffect(() => {
    // Reset form when modal opens/closes or action changes
    if (!actionBankAccount) {
      form.resetFields();
      setSearchCurrency("");
      setBankSelected(undefined);
      setAddNewBank(false);
      setSearchBank(undefined);
      setParams({ start, end });
      setParamSystem({ start, end });
      setOption(null);
      return;
    }

    // Auto refetch data when modal opens
    if (actionBankAccount) {
      // Refetch metadata
      refetchCurrency();
      refetchCountries();

      // Refetch banks list (only if userId exists)
      if (userId && !notGetUserBank) {
        refetchUserBank();
      }
      refetchSystemBank();

      // Refetch detail if EDIT mode
      if (actionBankAccount === "EDIT" && bankAccountId) {
        form.resetFields();
        refetchDetailBankAccount();
      }
    }

    // Handle ADD action - reset form
    if (actionBankAccount === "ADD") {
      form.resetFields();
      setSearchCurrency("");
      setBankSelected(undefined);
      setAddNewBank(false);
      setSearchBank(undefined);
      setParams({ start, end });
      setParamSystem({ start, end });
      setOption(null);
      return;
    }
  }, [
    actionBankAccount,
    form,
    refetchUserBank,
    refetchSystemBank,
    refetchDetailBankAccount,
    refetchCurrency,
    refetchCountries,
    bankAccountId,
    userId,
    notGetUserBank,
  ]);

  useEffect(() => {
    if (!bankAccountId || !detailBankAccount) return;
    const detailId = detailBankAccount?.data?.id;
    if (
      detailId !== undefined &&
      Number(detailId) !== Number(bankAccountId)
    ) {
      return;
    }

    form.setFieldsValue({
      bankId: detailBankAccount.data.bankId || "",
      accountNumber: !addNewBank
        ? detailBankAccount.data.accountNumber || ""
        : "",
      currencyCode: !addNewBank
        ? detailBankAccount.data.currencyCode || ""
        : undefined,
      abaRoutingNumber: !addNewBank
        ? detailBankAccount.data.abaRoutingNumber || ""
        : "",
      bankStateBranchNumber: !addNewBank
        ? detailBankAccount.data.bankStateBranchNumber || ""
        : "",
      branchNumber: !addNewBank
        ? detailBankAccount.data.branchNumber || ""
        : "",
    });
    setSearchCurrency(
      !addNewBank ? detailBankAccount.data.currencyCode || "" : ""
    );
    setBankSelected(detailBankAccount.data.bank || { id: detailBankAccount.data.bankId || "" });
  }, [bankAccountId, detailBankAccount, addNewBank, form]);

  useEffect(() => {
    if (!bankAccountId || !detailBankAccount) return;
    const detailId = detailBankAccount?.data?.id;
    if (
      detailId !== undefined &&
      Number(detailId) !== Number(bankAccountId)
    ) {
      return;
    }

    const allBanks = [
      ...(metadataUserBank?.data || []),
      ...(metadataSystemBank?.data || []),
    ];

    const found = allBanks.some(
      (item: any) => item.id === detailBankAccount.data.bankId
    );

    if (!found && detailBankAccount.data.bank) {
      setOption({
        role: detailBankAccount.data.bank.user?.role || "SYSTEM",
        value: detailBankAccount.data.bankId,
        label: detailBankAccount.data.bank.name,
      });
    } else {
      setOption(null);
    }
  }, [bankAccountId, detailBankAccount, metadataUserBank, metadataSystemBank]);

  useEffect(() => {
    const fields = addNewBank ? requiredFieldsAddNew : requiredFields;
    const values = form.getFieldsValue(fields);
    const allFilled = fields.every((field) => !!values[field]);
    setIsAllFieldsFilled(allFilled);
  }, [
    addNewBank,
    bankId,
    accountNumber,
    currencyCode,
    branchNumber,
    bankName,
    bankSwift,
    bankAddress,
    bankCountry,
    form,
  ]);

  const {
    modalProps: createBankModalProps,
    formProps: createBankFormProps,
    show: createBankModalShow,
    formLoading: createBankFormLoading,
    submit: createBankFormSubmit,
    close: createBankModalClose,
  } = useModalForm({
    warnWhenUnsavedChanges: false,
    action: "create",
  });

  const handleBankCreated = useCallback(
    (createdBank?: any) => {
      refetchSystemBank();

      const newBankId = createdBank?.id;
      if (!newBankId) {
        return;
      }

      setBankSelected(createdBank);
      form.setFieldValue("bankId", newBankId);
      setOption({
        role: createdBank?.user?.role || "SYSTEM",
        value: newBankId,
        label: createdBank?.name || "",
      });
    },
    [form, refetchSystemBank]
  );

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmitBankAccount}
        onValuesChange={clearFieldErrorMessageOnChange(form)}
      >
        <div style={{ marginBottom: "16px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <Typography.Text
              style={{ lineHeight: "32px", fontWeight: 500, fontSize: 16 }}
            >
              Bank Details
            </Typography.Text>
            {addNewBank ? (
              <Button
                onClick={() => {
                  setAddNewBank(false);
                  form.setFieldsValue({
                    bankName: undefined,
                    bankSwift: undefined,
                    bankAddress: undefined,
                    bankCountry: undefined,
                  });
                }}
              >
                Cancel
              </Button>
            ) : null}
          </div>
          {addNewBank ? (
            <>
              <Form.Item
                label="Bank Name"
                name="bankName"
                rules={[{ required: true, message: "This field is required", whitespace: true }]}
              >
                <Input
                  placeholder="Enter the full name of the bank"
                  style={buttonStyle}
                  size="large"
                />
              </Form.Item>
              <Form.Item
                label="Bank Swift"
                name="bankSwift"
                rules={[{ required: true, message: "This field is required", whitespace: true }]}
              >
                <Input
                  placeholder="Enter the bank's SWIFT code"
                  style={buttonStyle}
                  size="large"
                />
              </Form.Item>
              <Form.Item
                label="Bank Address"
                name="bankAddress"
                rules={[{ required: true, message: "This field is required", whitespace: true }]}
              >
                <Input
                  placeholder="Enter the registered address of the bank"
                  style={buttonStyle}
                  size="large"
                />
              </Form.Item>
              <Form.Item
                label="Bank Country"
                name="bankCountry"
                rules={[{ required: true, message: "This field is required" }]}
              >
                <Select
                  placeholder="Select the country where the bank is located"
                  showSearch
                  filterOption={(input, option) =>
                    ((option?.label as string) ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  size="large"
                  style={buttonStyle}
                >
                  {metadataCountries.map(
                    (item: { value: string; label: string }) => (
                      <Select.Option key={item.value} value={item.value}>
                        {item.label}
                      </Select.Option>
                    )
                  )}
                </Select>
              </Form.Item>
            </>
          ) : (
            <div
              style={{
                display: "flex",
                width: "100%",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  flexDirection: "row",
                  alignItems: "flex-start",
                }}
              >
                <Form.Item
                  label="Bank"
                  name="bankId"
                  rules={[{ required: true, message: "This field is required" }]}
                  validateTrigger="onBlur"
                  style={{ flex: 1 }}
                >
                  {notGetUserBank ? (
                    <>
                      <Select
                        allowClear
                        className="beneficiary-bank-account"
                        placeholder="Choose a bank"
                        suffixIcon={bankSelected ? null : <DownOutlined />}
                        showSearch
                        onChange={(id) => {
                          const selected = [
                            ...(metadataSystemBank?.data || []),
                          ].find((item: any) => item.id === id);
                          setBankSelected(selected);
                          if (!id) {
                            setBankSelected(undefined);
                          }
                        }}
                        onSearch={(value: string) => {
                          setSearchBank(value);
                          setParamSystem({ start: 0, end: 10 });
                        }}
                        filterOption={() => true}
                      >
                        {option && option.role !== "USER" ? (
                          <Select.Option
                            key={option.value}
                            value={option.value}
                            label={option.label}
                          >
                            <span style={{ whiteSpace: "nowrap" }}>
                              {option.label}
                            </span>
                            {bankSelected?.id === option.value && (
                              <CheckOutlined style={{ marginLeft: 8 }} />
                            )}
                          </Select.Option>
                        ) : null}
                        {Array.isArray(metadataSystemBank?.data) &&
                          metadataSystemBank.data.map((item: any) => (
                            <Select.Option key={item.id} value={item.id}>
                              <span style={{ whiteSpace: "nowrap" }}>
                                {item.name}
                              </span>
                              {bankSelected?.id === item.id && (
                                <CheckOutlined style={{ marginLeft: 8 }} />
                              )}
                            </Select.Option>
                          ))}
                        {metadataSystemBank?.data?.length === paramSystem.end && (
                          <Select.Option key="load-more-system" disabled>
                            <span
                              style={{
                                fontSize: 14,
                                color: "rgba(29, 171, 241, 1)",
                                fontWeight: 600,
                                cursor: "pointer",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setParamSystem((prev) => ({
                                  ...prev,
                                  end: prev.end + 10,
                                }));
                              }}
                            >
                              Load more
                            </span>
                          </Select.Option>
                        )}
                      </Select>
                    </>
                  ) : (
                    <>
                      <Select
                        allowClear
                        className="beneficiary-bank-account"
                        placeholder="Choose a bank"
                        suffixIcon={bankSelected ? null : <DownOutlined />}
                        showSearch
                        onChange={(id) => {
                          const selected = [
                            ...(metadataSystemBank?.data || []),
                            ...(metadataUserBank?.data || []),
                          ].find((item: any) => item.id === id);
                          setBankSelected(selected);
                          if (!id) {
                            setBankSelected(undefined);
                          }
                        }}
                        onSearch={(value: string) => {
                          setSearchBank(value);
                          setParams({ start: 0, end: 10 });
                          setParamSystem({ start: 0, end: 10 });
                        }}
                        filterOption={() => true}
                      >
                        <Select.OptGroup label="User Bank">
                          {option && option.role === "USER" ? (
                            <Select.Option
                              key={option.value}
                              value={option.value}
                              label={option.label}
                            >
                              <span style={{ whiteSpace: "nowrap" }}>
                                {option.label}
                              </span>
                              {bankSelected?.id === option.value && (
                                <CheckOutlined style={{ marginLeft: 8 }} />
                              )}
                            </Select.Option>
                          ) : null}
                          {Array.isArray(metadataUserBank?.data) &&
                            metadataUserBank.data.map((item: any) => (
                              <Select.Option key={item.id} value={item.id}>
                                <span style={{ whiteSpace: "nowrap" }}>
                                  {item.name}
                                </span>
                                {bankSelected?.id === item.id && (
                                  <CheckOutlined style={{ marginLeft: 8 }} />
                                )}
                              </Select.Option>
                            ))}
                          {metadataUserBank?.data?.length === params.end && (
                            <Select.Option key="load-more-user" disabled>
                              <span
                                style={{
                                  fontSize: 14,
                                  color: "rgba(29, 171, 241, 1)",
                                  fontWeight: 600,
                                  cursor: "pointer",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setParams((prev) => ({
                                    ...prev,
                                    end: prev.end + 10,
                                  }));
                                }}
                              >
                                Load more
                              </span>
                            </Select.Option>
                          )}
                        </Select.OptGroup>
                        <Select.OptGroup label="System Banks">
                          {option && option.role !== "USER" ? (
                            <Select.Option
                              key={option.value}
                              value={option.value}
                              label={option.label}
                            >
                              <span style={{ whiteSpace: "nowrap" }}>
                                {option.label}
                              </span>
                              {bankSelected?.id === option.value && (
                                <CheckOutlined style={{ marginLeft: 8 }} />
                              )}
                            </Select.Option>
                          ) : null}
                          {Array.isArray(metadataSystemBank?.data) &&
                            metadataSystemBank.data.map((item: any) => (
                              <Select.Option key={item.id} value={item.id}>
                                <span style={{ whiteSpace: "nowrap" }}>
                                  {item.name}
                                </span>
                                {bankSelected?.id === item.id && (
                                  <CheckOutlined style={{ marginLeft: 8 }} />
                                )}
                              </Select.Option>
                            ))}
                          {metadataSystemBank?.data?.length ===
                            paramSystem.end && (
                              <Select.Option key="load-more-system" disabled>
                                <span
                                  style={{
                                    fontSize: 14,
                                    color: "rgba(29, 171, 241, 1)",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setParamSystem((prev) => ({
                                      ...prev,
                                      end: prev.end + 10,
                                    }));
                                  }}
                                >
                                  Load more
                                </span>
                              </Select.Option>
                            )}
                        </Select.OptGroup>
                      </Select>
                    </>
                  )}
                </Form.Item>
                {notGetUserBank && (
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => createBankModalShow()}
                    style={{ minWidth: 86, height: 32, marginTop: 30, marginLeft: 12 }}
                  >
                    Add
                  </Button>
                )}
              </div>
              {(actionBankAccount === "EDIT" ||
                (actionBankAccount === "ADD" && bankSelected)) && (
                  <div
                    style={{
                      marginTop: "12px",
                      padding: "12px",
                      backgroundColor: "#f5f5f5",
                      borderRadius: "4px",
                    }}
                  >
                    <Typography.Text
                      style={{
                        display: "block",
                        fontSize: 14,
                        fontWeight: 400,
                        marginBottom: "4px",
                      }}
                    >{`Name: ${bankSelected?.name ||
                      detailBankAccount?.data?.bank?.name ||
                      ""
                      }`}</Typography.Text>
                    <Typography.Text
                      style={{
                        display: "block",
                        fontSize: 14,
                        fontWeight: 400,
                        marginBottom: "4px",
                      }}
                    >{`Swift: ${bankSelected?.swift ||
                      detailBankAccount?.data?.bank?.swift ||
                      ""
                      }`}</Typography.Text>
                    <Typography.Text
                      style={{
                        display: "block",
                        fontSize: 14,
                        fontWeight: 400,
                      }}
                    >{`Address: ${bankSelected?.legalAddress ||
                      detailBankAccount?.data?.bank?.legalAddress ||
                      ""
                      }`}</Typography.Text>
                  </div>
                )}
            </div>
          )}
        </div>
        <div style={{ marginBottom: "16px" }}>
          <Typography.Text
            style={{
              lineHeight: "32px",
              fontWeight: 500,
              fontSize: 16,
              display: "block",
              marginBottom: "16px",
            }}
          >
            Account Details
          </Typography.Text>
          <Form.Item
            label="Account No or IBAN"
            name="accountNumber"
            validateTrigger="onBlur"
            rules={accountNumberRules}
          >
            <Input
              placeholder="Enter account number or IBAN"
              style={buttonStyle}
              size="large"
            />
          </Form.Item>
          <Form.Item
            label="Currency"
            name="currencyCode"
            validateTrigger="onBlur"
            rules={[{ required: true, message: "This field is required" }]}
          >
            <Select
              placeholder="Select the account currency"
              suffixIcon={<DownOutlined />}
              onChange={handleChangeCurrency}
              optionLabelProp="label"
              size="large"
              style={buttonStyle}
            >
              {metadataCurrency.map((item: { value: string; label: string }) => (
                <Select.Option key={item.value} value={item.value}>
                  <span
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                      gap: 2,
                    }}
                  >
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                      }}
                    >
                      {item.label}
                    </span>
                    {searchCurrency === item.value && (
                      <CheckOutlined style={{ flexShrink: 0 }} />
                    )}
                  </span>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="ABA Routing Number"
            name="abaRoutingNumber"
            validateTrigger="onBlur"
            rules={[
              {
                max: 25,
                message: "Maximum 25 characters",
                validateTrigger: "onBlur",
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const trimmed = value.trim();
                  if (trimmed.length < 4) {
                    return Promise.reject(new Error("Minimum 4 characters"));
                  }

                  return Promise.resolve();
                },
              },
              onlyLatinCharsDigitsRule,
            ]}
          >
            <Input
              placeholder="Enter the ABA routing number"
              onKeyDown={(e) => {
                const target = e.currentTarget as HTMLInputElement;
                if (e.key === " " && target.selectionStart === 0) {
                  e.preventDefault();
                }
              }}
              style={buttonStyle}
              size="large"
            />
          </Form.Item>
          <Typography.Text
            style={{
              color: "#F28A28",
              fontSize: 12,
              fontWeight: 400,
              lineHeight: "16px",
              marginTop: "-16px",
              display: "block",
              marginBottom: "16px",
            }}
          >
            If not available, please key in 000000.
          </Typography.Text>
          <Form.Item
            label="Bank State Branch Number"
            name="bankStateBranchNumber"
            validateTrigger="onBlur"
            rules={[
              {
                max: 25,
                message: "Maximum 25 characters",
                validateTrigger: "onBlur",
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const trimmed = value.trim();
                  if (trimmed.length < 4) {
                    return Promise.reject(new Error("Minimum 4 characters"));
                  }

                  return Promise.resolve();
                },
              },
              onlyLatinCharsDigitsRule,
            ]}
          >
            <Input
              placeholder="Enter the BSB number"
              onKeyDown={(e) => {
                const target = e.currentTarget as HTMLInputElement;
                if (e.key === " " && target.selectionStart === 0) {
                  e.preventDefault();
                }
              }}
              style={buttonStyle}
              size="large"
            />
          </Form.Item>
          <Typography.Text
            style={{
              color: "#F28A28",
              fontSize: 12,
              fontWeight: 400,
              lineHeight: "16px",
              marginTop: "-16px",
              display: "block",
              marginBottom: "16px",
            }}
          >
            If not available, please key in 000000.
          </Typography.Text>
          <Form.Item
            label="Branch Number"
            name="branchNumber"
            validateTrigger="onBlur"
            rules={branchNumberRules}
          >
            <Input
              placeholder="Enter the branch number"
              style={buttonStyle}
              size="large"
            />
          </Form.Item>
          <Typography.Text
            style={{
              color: "#F28A28",
              fontSize: 12,
              fontWeight: 400,
              lineHeight: "16px",
              marginTop: "-16px",
              display: "block",
              marginBottom: "16px",
            }}
          >
            If not available, please key in 000000.
          </Typography.Text>
        </div>
        <div
          style={{
            display: "flex",
            gap: "12px",
            marginTop: "24px",
            justifyContent: "flex-end",
          }}
        >
          <Button onClick={handleCancel} size="large" disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={() => form.submit()}
            size="large"
            loading={isLoading}
            disabled={!isAllFieldsFilled}
          >
            Save
          </Button>
        </div>
      </Form>
      <NewBankModalCreate
        closeModal={createBankModalClose}
        formProps={createBankFormProps}
        modalProps={createBankModalProps}
        formIsLoading={createBankFormLoading}
        formSubmit={createBankFormSubmit}
        refetchList={handleBankCreated}
        customerId={details?.id}
      />
    </>
  );
};

export default FormBank;
